import React, { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode"; // Corrected import for jwt-decode
import Cookies from "js-cookie"; // Import the js-cookie library
import Loading from "../components/Loading";
import { toast } from "react-toastify";

import {
  fetchAllPateintData,
  fetchMedicalProfileDetails,
  fetchOpdData,
  fetchUpcomingAppointments,
  fetchUserAllDetails,
} from "../services/api";
import API from "../services/interceptor";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // *********** Login User ***********
  const [loginUser, setLoginUser] = useState(null);
  const [loginLoading, setLoginLoading] = useState(true); // To show loading until we verify the token

  // *********** Complete User Profile State (replaces previous userDetails) ***********
  const [currentUserProfile, setCurrentUserProfile] = useState(null); // Complete user profile data
  const [currentUserProfileLoading, setCurrentUserProfileLoading] = useState(false);
  const [currentUserProfileError, setCurrentUserProfileError] = useState(null);

  // *********** Upcomming appointment data state **************
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState(null);

  // *********** All Visits data state (NEW) ***********
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsError, setVisitsError] = useState(null);


  // *********** All patient data state ***************
  const [allPatients, setAllPatients] = useState([]);
  const [allPatientsLoading, setAllPatientsLoading] = useState(false);
  const [allPatientsError, setAllPatientsError] = useState(null);

  // *********** OPD data state ***********
  const [opdData, setOpdData] = useState([]);
  const [opdLoading, setOpdLoading] = useState(false);
  const [opdError, setOpdError] = useState(null);

  // *********** Medical Profile details state ***********
  const [medicalProfileDetails, setMedicalProfileDetails] = useState([]);
  const [medicalProfileDetailsLoading, setMedicalProfileDetailsLoading] = useState(false);
  const [medicalProfileDetailsError, setMedicalProfileDetailsError] = useState(null);


  // ***************** Drop Down Menu *****************
  const [chiefComplaint, setChiefComplaint] = useState([]);
  const [visitReason, setVisitReason] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [dentalReasons, setDentalReasons] = useState([]);
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState([]);
  const [treatmentPlan, setTreatmentPlan] = useState([]);
  const [medicineName, setMedicineName] = useState([]);


  // --- NEW DROPDOWN STATES ADDED ---
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState([]);
  const [proceduresPerformedToday, setProceduresPerformedToday] = useState([]);
  const [allergiesName, setAllergiesName] = useState([]);
  const [allergiesReaction, setAllergiesReaction] = useState([]);
  const [documentName, setDocumentName] = useState([]);

  const [dropDownLoading, setDropDownLoading] = useState(false);
  const [dropDownError, setDropDownError] = useState(null);

  



  // Check for token and user data in cookies when the component mounts
  useEffect(() => {
    const token = Cookies.get("token");

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Current time in seconds

        // Check if token is expired
        if (decodedToken.exp < currentTime) {
          logout(); // Token is expired, log out the user
        } else {
          setLoginUser(decodedToken); // Set user info from the decoded token
          // Automatically fetch complete user profile after setting login user
          if (decodedToken.id) {
            getCurrentUserProfile(decodedToken.id);
          }
        }
      } catch (error) {
        console.error("Invalid token", error);
        logout(); // Logout in case of invalid token
      }
      try{
        getDropDownMenu(); // Fetch dropdown menu data
      }
      catch(error){
        console.error("Error fetching dropdown menu on init:", error);
      }
    }
    setLoginLoading(false); // Set loading to false after the check is complete
  }, []);

  // ***************** Login *****************
  const login = (token) => {
    try {
      Cookies.set("token", token, { expires: 7 }); // Set the token in cookies with an expiration of 7 days

      const decodedToken = jwtDecode(token);
      console.log("Decoded Token:", decodedToken);
      setLoginUser(decodedToken);
      
      // Clear any previous errors
      setCurrentUserProfileError(null);
      
      // Automatically fetch complete user profile after login
      if (decodedToken.id) {
        getCurrentUserProfile(decodedToken.id);
      }
      
      console.log(decodedToken);
    } catch (error) {
      console.error("Invalid token provided during login", error);
      toast.error("Invalid authentication token", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    }
  };

  // ***************** Logout *****************
  const logout = () => {
    try {
      Cookies.remove("token"); // Remove the token from cookies
      setLoginUser(null);
      setCurrentUserProfile(null); // Clear user profile data
      
      // Clear all other state data
      setAppointments([]);
      setAllPatients([]);
      setOpdData([]);
      setMedicalProfileDetails([]);
      
      // Clear all error states
      setCurrentUserProfileError(null);
      setAppointmentsError(null);
      setAllPatientsError(null);
      setOpdError(null);
      setMedicalProfileDetailsError(null);
    } catch (error) {
      console.error("Error during logout", error);
    }
  };

  // ***************** Function to fetch complete current user profile **********************
  const getCurrentUserProfile = async (userId) => {
    if (!userId) {
      console.error("No user ID provided for profile fetch");
      return;
    }

    setCurrentUserProfileLoading(true);
    setCurrentUserProfileError(null);
    try {
      const data = await fetchUserAllDetails(userId); // Call the API function
      
      // Validate the data structure
      if (data && typeof data === 'object') {
        setCurrentUserProfile(data);
        console.log("Complete User Profile:", data);
      } else {
        throw new Error("Invalid user profile data received");
      }
    } catch (error) {
      const errorMessage = error.message || error || "Failed to fetch user profile";
      setCurrentUserProfileError(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setCurrentUserProfileLoading(false);
    }
  };

  // ***************** Function to fetch appointments data **********************
  const getUpcomingAppointments = async () => {
    setAppointmentsLoading(true);
    setAppointmentsError(null); // Reset error state
    try {
      const data = await fetchUpcomingAppointments(); // Call the API function
      setAppointments(Array.isArray(data) ? data : []);
    } catch (error) {
      const errorMessage = error.message || error || "Failed to fetch appointments";
      setAppointmentsError(errorMessage); // Update error state
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setAppointmentsLoading(false); // Reset loading state
    }
  };

  // ***************** Function to fetch all visits data (NEW) **********************
  const getAllVisits = async () => {
    setVisitsLoading(true);
    setVisitsError(null);
    try {
      // Directly using the API interceptor as per the visit management component
      const response = await API.get('/api/visits');
      const data = response.data;
      setVisits(Array.isArray(data) ? data : []);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch visits";
      setVisitsError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setVisitsLoading(false);
    }
  };

  // ***************** function to fetch all patient data *****************
  const getAllPateintData = async () => {
    setAllPatientsLoading(true);
    setAllPatientsError(null);
    try {
      const data = await fetchAllPateintData(); // Call the API function
      setAllPatients(data?.data || []);
    } catch (error) {
      const errorMessage = error.message || error || "Failed to fetch patient data";
      setAllPatientsError(errorMessage); // Update error state
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setAllPatientsLoading(false); // Reset loading state
    }
  };

  // ***************** function to fetch OPD Data *****************
  const getOPDData = async () => {
    setOpdLoading(true);
    setOpdError(null);
    try {
      const data = await fetchOpdData(); // Call the API function
      setOpdData(Array.isArray(data) ? data : []);
    } catch (error) {
      const errorMessage = error.message || error || "Failed to fetch OPD data";
      setOpdError(errorMessage); // Update error state
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setOpdLoading(false); // Reset loading state
    }
  };

  // ***************** function to fetch medical profile details *****************
  const getMedicalProfileDetails = async (loginUserID) => {
    if (!loginUserID) {
      console.error("No user ID provided for medical profile fetch");
      return;
    }

    setMedicalProfileDetailsLoading(true);
    setMedicalProfileDetailsError(null);
    try {
      const data = await fetchMedicalProfileDetails(loginUserID); // Call the API function
      setMedicalProfileDetails(Array.isArray(data) ? data : []);
    } catch (error) {
      const errorMessage = error.message || error || "Failed to fetch medical profile";
      setMedicalProfileDetailsError(errorMessage); // Update error state
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setMedicalProfileDetailsLoading(false); // Reset loading state
    }
  };

    // ***************** function to fetch drop down menu *****************
  // ***************** function to fetch drop down menu *****************
const getDropDownMenu = async (loginUserID) => {
  setDropDownLoading(true);
  setDropDownError(null);

  try {
    // If backend needs userId, add as query param
    

    const res =  await API.get("/api/dropdown")

    const payload = res.data || {};

    console.log("Dropdown Menu Data:", payload);      

    // Safely set dropdown states
    setChiefComplaint(Array.isArray(payload.chiefComplaint) ? payload.chiefComplaint : []);
    setVisitReason(Array.isArray(payload.visitReason) ? payload.visitReason : []);
    setReasonOptions(Array.isArray(payload.reasonOptions) ? payload.reasonOptions : []);
    setTimeBlocks(Array.isArray(payload.timeBlocks) ? payload.timeBlocks : []);
    setMedicalConditions(Array.isArray(payload.medicalConditions) ? payload.medicalConditions : []);
    setDentalReasons(Array.isArray(payload.dentalReasons) ? payload.dentalReasons : []);
    setPrimaryDiagnosis(Array.isArray(payload.primaryDiagnosis) ? payload.primaryDiagnosis : []);
    setTreatmentPlan(Array.isArray(payload.treatmentPlan) ? payload.treatmentPlan : []);
    setMedicineName(Array.isArray(payload.medicineName) ? payload.medicineName : []);

    // --- SETTING NEW DROPDOWN STATES ---
      setSecondaryDiagnosis(Array.isArray(payload.secondaryDiagnosis) ? payload.secondaryDiagnosis : []);
      setProceduresPerformedToday(Array.isArray(payload.proceduresPerformedToday) ? payload.proceduresPerformedToday : []);
      setAllergiesName(Array.isArray(payload.allergiesName) ? payload.allergiesName : []);
      setAllergiesReaction(Array.isArray(payload.allergiesReaction) ? payload.allergiesReaction : []);
      setDocumentName(Array.isArray(payload.documentName) ? payload.documentName : []);


    return payload; // return for use elsewhere if needed
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error.message ||
      "Failed to fetch drop down menu";

    console.error("getDropDownMenu error:", errorMessage);
    setDropDownError(errorMessage);

    toast.error(errorMessage, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: true,
      theme: "dark",
    });

    return null;
  } finally {
    setDropDownLoading(false);
  }
};


  // ***************** Helper function to get user display name - FIXED *****************
  const getUserDisplayName = () => {
    // Show loading state
    if (currentUserProfileLoading) {
      return "Loading...";
    }

    // If no login user, return default
    if (!loginUser) {
      return "Guest";
    }

    // If we have loginUser but no profile yet, return role-based default
    if (!currentUserProfile) {
      return loginUser?.role ? `${loginUser.role.charAt(0).toUpperCase()}${loginUser.role.slice(1)}` : "User";
    }

    // Safe property access with multiple fallbacks
    try {
      const userDetails = currentUserProfile?.userDetails;
      const profileName = userDetails?.name;
      
      if (profileName && typeof profileName === 'string') {
        return profileName;
      }

      // Fallback to other possible name fields
      if (currentUserProfile?.name && typeof currentUserProfile.name === 'string') {
        return currentUserProfile.name;
      }

      // Fallback based on role
      switch (loginUser.role) {
        case "patient":
          return "Patient";
        case "doctor":
          return "Doctor";
        case "receptionist":
          return "Receptionist";
        case "admin":
          return "Administrator";
        default:
          return loginUser.role ? `${loginUser.role.charAt(0).toUpperCase()}${loginUser.role.slice(1)}` : "User";
      }
    } catch (error) {
      console.error("Error getting user display name:", error);
      return loginUser?.role || "User";
    }
  };

  

  // ***************** Helper function to get user email - FIXED *****************
  const getUserEmail = () => {
    try {
      // Try multiple sources for email
      const email = loginUser?.email || 
                   currentUserProfile?.email || 
                   currentUserProfile?.userDetails?.email ||
                   "";
      
      return typeof email === 'string' ? email : "";
    } catch (error) {
      console.error("Error getting user email:", error);
      return "";
    }
  };

  // Safe state setters with error handling
  const safeSetCurrentUserProfile = (profile) => {
    try {
      setCurrentUserProfile(profile);
    } catch (error) {
      console.error("Error setting user profile:", error);
    }
  };

  const safeSetCurrentUserProfileLoading = (loading) => {
    try {
      setCurrentUserProfileLoading(Boolean(loading));
    } catch (error) {
      console.error("Error setting profile loading state:", error);
    }
  };

  const safeSetCurrentUserProfileError = (error) => {
    try {
      setCurrentUserProfileError(error);
    } catch (err) {
      console.error("Error setting profile error state:", err);
    }
  };

  if (loginLoading) {
    return <Loading />; // Render a loading state until token is checked
  }

  // Context value with safe defaults
  const contextValue = {
    // User authentication
    loginUser: loginUser || null,
    login,
    logout,

    // Complete user profile
    currentUserProfile: currentUserProfile || null,
    currentUserProfileLoading: Boolean(currentUserProfileLoading),
    currentUserProfileError: currentUserProfileError || null,
    getCurrentUserProfile,
    setCurrentUserProfile: safeSetCurrentUserProfile,
    setCurrentUserProfileLoading: safeSetCurrentUserProfileLoading,
    setCurrentUserProfileError: safeSetCurrentUserProfileError,

    // Helper functions for user info
    getUserDisplayName,
    getUserEmail,

    // Appointments
    getUpcomingAppointments,
    appointments: Array.isArray(appointments) ? appointments : [],
    appointmentsLoading: Boolean(appointmentsLoading),
    appointmentsError: appointmentsError || null,
    setAppointments: (appts) => setAppointments(Array.isArray(appts) ? appts : []),
    setAppointmentsError,
    setAppointmentsLoading: (loading) => setAppointmentsLoading(Boolean(loading)),

    // All patients
    getAllPateintData,
    allPatients: Array.isArray(allPatients) ? allPatients : [],
    allPatientsLoading: Boolean(allPatientsLoading),
    allPatientsError: allPatientsError || null,
    setAllPatients: (patients) => setAllPatients(Array.isArray(patients) ? patients : []),
    setAllPatientsError,

    // OPD data
    getOPDData,
    opdData: Array.isArray(opdData) ? opdData : [],
    opdLoading: Boolean(opdLoading),
    opdError: opdError || null,
    setOpdData: (data) => setOpdData(Array.isArray(data) ? data : []),
    setOpdLoading: (loading) => setOpdLoading(Boolean(loading)),

    // Medical profile details
    getMedicalProfileDetails,
    medicalProfileDetails: Array.isArray(medicalProfileDetails) ? medicalProfileDetails : [],
    medicalProfileDetailsLoading: Boolean(medicalProfileDetailsLoading),
    medicalProfileDetailsError: medicalProfileDetailsError || null,
    setMedicalProfileDetailsError,
    setMedicalProfileDetailsLoading: (loading) => setMedicalProfileDetailsLoading(Boolean(loading)),
    setMedicalProfileDetails: (details) => setMedicalProfileDetails(Array.isArray(details) ? details : []),

    // Backward compatibility (keeping old userDetails for existing components)
    userDetails: currentUserProfile || null,
    userDetailsLoading: Boolean(currentUserProfileLoading),
    userDetailsError: currentUserProfileError || null,
    getUserDetails: getCurrentUserProfile,
    setUserDetailsLoading: safeSetCurrentUserProfileLoading,
    setUserDetailsError: safeSetCurrentUserProfileError,

    getDropDownMenu,
    // Drop Down Menu States
    chiefComplaint: Array.isArray(chiefComplaint) ? chiefComplaint : [],
    visitReason: Array.isArray(visitReason) ? visitReason : [],
    reasonOptions: Array.isArray(reasonOptions) ? reasonOptions : [], 
    timeBlocks: Array.isArray(timeBlocks) ? timeBlocks : [],
    medicalConditions: Array.isArray(medicalConditions) ? medicalConditions : [],
    dentalReasons: Array.isArray(dentalReasons) ? dentalReasons : [],
    primaryDiagnosis: Array.isArray(primaryDiagnosis) ? primaryDiagnosis : [],
    treatmentPlan: Array.isArray(treatmentPlan) ? treatmentPlan : [],
    medicineName: Array.isArray(medicineName) ? medicineName : [],

    // --- ADDING NEW DROPDOWN VALUES TO CONTEXT ---
    secondaryDiagnosis: Array.isArray(secondaryDiagnosis) ? secondaryDiagnosis : [],
    proceduresPerformedToday: Array.isArray(proceduresPerformedToday) ? proceduresPerformedToday : [],
    allergiesName: Array.isArray(allergiesName) ? allergiesName : [],
    allergiesReaction: Array.isArray(allergiesReaction) ? allergiesReaction : [],
    documentName: Array.isArray(documentName) ? documentName : [],

    dropDownLoading: Boolean(dropDownLoading),
    dropDownError: dropDownError || null,

     // All Visits (NEW)
    getAllVisits,
    visits: Array.isArray(visits) ? visits : [],
    visitsLoading: Boolean(visitsLoading),
    visitsError: visitsError || null,
    setVisits: (data) => setVisits(Array.isArray(data) ? data : []),
    setVisitsError,
    setVisitsLoading: (loading) => setVisitsLoading(Boolean(loading)),
    
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};